import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Step,
  StepButton,
  Stepper,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SaveIcon from '@mui/icons-material/Save';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { Helmet } from 'react-helmet-async';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

import config from '@src/config';
import leadGurusLogo from '@src/assets/logo-full-dark.png';
import { toast } from '@src/components/core/toaster';
import { fetchClient } from '@src/api/actions/clients';
import defaultClient from '@src/api/clients/default';
import { useAuth } from '@src/context';

const CRM_OPTIONS = [
  { value: 'builder-prime', label: 'Builder Prime' },
  { value: 'i360', label: 'Improveit360 (i360)' },
  { value: 'jobnimbus', label: 'JobNimbus' },
  { value: 'leadconduit', label: 'LeadConduit' },
  { value: 'leadperfection', label: 'LeadPerfection' },
  { value: 'marketsharp', label: 'MarketSharp' },
  { value: 'opta', label: 'Opta' },
  { value: 'salesforce', label: 'Salesforce' },
  { value: 'servicetitan', label: 'ServiceTitan' },
  { value: 'other', label: 'Other' },
  { value: 'none', label: 'No CRM' },
];

const STEPS = [
  { key: 'basics', label: 'Contact Info' },
  { key: 'billing', label: 'Billing' },
  { key: 'budgets', label: 'Confirm Budgets' },
  { key: 'assets', label: 'Assets' },
  { key: 'offers', label: 'Offers & Messaging' },
  { key: 'crm', label: 'CRM & Routing' },
  { key: 'review', label: 'Review' },
];

// Approvals step is excluded from the wizard — it happens later when ads/landing pages are built

function fetchOnboarding(slug) {
  return defaultClient.request({ url: `clients/${slug}/onboarding/` });
}
function saveOnboarding(slug, payload) {
  return defaultClient.request({ url: `clients/${slug}/onboarding/`, method: 'PATCH', payload });
}
function fetchTimeline(slug) {
  return defaultClient.request({ url: `clients/${slug}/onboarding/timeline/` });
}

export default function OnboardingWizardPage() {
  const { clientSlug } = useParams();
  const navigate = useNavigate();
  const { state: authState, logout } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [offerOptions, setOfferOptions] = useState({});
  const [clientVerticals, setClientVerticals] = useState([]);

  // Creative review state
  const [creativeReviews, setCreativeReviews] = useState([]);
  const [reviewAction, setReviewAction] = useState(null);
  const [reviewFeedback, setReviewFeedback] = useState('');
  const [reviewName, setReviewName] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [timeline, setTimeline] = useState(null);

  // Landing page approval state
  const [landingPages, setLandingPages] = useState([]);
  const [lpSubmitting, setLpSubmitting] = useState(false);

  // Invite members state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);

  // Form state — mirrors the onboarding payload structure
  const [form, setForm] = useState({
    basics: { firstName: '', lastName: '', email: '', position: '' },
    secondaryPocs: [],
    billingContact: { name: '', email: '', phone: '', addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '', country: '' },
    billingAcknowledged: false,
    assets_folder_link: '',
    use_stock_assets: false,
    assetFiles: [],
    salesSchedule: '',
    evergreenOffer: '',
    financingOffer: '',
    crm: { choice: '', other: '', wantsGoogleSheet: false, emailNotifications: false, notificationEmails: '', postingNotes: '' },
    offers: {},
    launchDate: null,
    bathManufacturers: [],
  });

  // Load existing data
  useEffect(() => {
    if (!clientSlug) return;
    setLoading(true);
    Promise.all([
      fetchClient(clientSlug).catch(() => null),
      fetchOnboarding(clientSlug).catch(() => null),
      fetchTimeline(clientSlug).catch(() => null),
    ]).then(([clientRes, onbRes, tlRes]) => {
      const c = clientRes?.data;
      const onb = onbRes?.data;

      // Branded flows route to their tailored wizard.
      if (onb?.application_source === 'five_star') {
        navigate(`/onboarding/five-star/${clientSlug}`, { replace: true });
        return;
      }

      if (c) setClient(c);
      if (tlRes?.data) {
        setTimeline(tlRes.data);
        setLandingPages(tlRes.data.landing_pages || []);
      }

      if (onb) {
        // Load offer options + verticals
        if (onb.offer_options) setOfferOptions(onb.offer_options);
        if (onb.client_verticals) setClientVerticals(onb.client_verticals);

        // Load existing offers into keyed form structure
        const existingOffers = {};
        if (onb.client_offers) {
          onb.client_offers.forEach((o) => {
            const key = `${o.vertical_id}_${o.offer_type}`;
            existingOffers[key] = {
              vertical_id: o.vertical_id,
              offer_type: o.offer_type,
              ad_offer_option_id: o.ad_offer_option_id,
              lp_offer_option_id: o.lp_offer_option_id,
              financing_offer_option_id: o.financing_offer_option_id,
              custom_label: o.custom_label || '',
              financing_details: o.financing_details || '',
            };
          });
        }

        setForm((prev) => ({
          ...prev,
          basics: {
            firstName: onb.basics?.firstName || prev.basics.firstName,
            lastName: onb.basics?.lastName || prev.basics.lastName,
            email: onb.basics?.email || prev.basics.email,
            position: onb.basics?.position || prev.basics.position,
          },
          secondaryPocs: onb.secondary_pocs || prev.secondaryPocs,
          billingContact: {
            name: onb.billing_contact?.name || '',
            email: onb.billing_contact?.email || '',
            phone: onb.billing_contact?.phone || '',
            addressLine1: onb.billing_contact?.addressLine1 || '',
            addressLine2: onb.billing_contact?.addressLine2 || '',
            city: onb.billing_contact?.city || '',
            state: onb.billing_contact?.state || '',
            postalCode: onb.billing_contact?.postalCode || '',
            country: onb.billing_contact?.country || '',
          },
          billingAcknowledged: !!onb.billing_acknowledged,
          assets_folder_link: onb.assets_folder_link || '',
          use_stock_assets: !!onb.use_stock_assets,
          offers: onb.offers || {},
          salesSchedule: onb.sales_schedule || '',
          evergreenOffer: onb.evergreen_offer || '',
          financingOffer: onb.financing_offer || '',
          crm: {
            choice: onb.crm_choice || '',
            other: onb.crm_other || '',
            wantsGoogleSheet: !!onb.crm_wants_google_sheet,
            emailNotifications: !!onb.crm_email_notifications,
            notificationEmails: onb.crm_notification_emails || '',
            notificationEmailList: (() => {
              const emails = (onb.crm_notification_emails || '').split(',').map((e) => e.trim()).filter(Boolean);
              return emails.length ? emails : [''];
            })(),
            postingNotes: onb.crm_posting_notes || '',
          },
          launchDate: onb.launch_goal ? dayjs(onb.launch_goal) : null,
        }));

        // Load offer options and client verticals
        if (onb.offer_options) setOfferOptions(onb.offer_options);
        if (onb.client_verticals) setClientVerticals(onb.client_verticals);
      }
      // Load creative reviews
      defaultClient.request({ url: `app1/creative-review/${clientSlug}/` })
        .then((res) => setCreativeReviews(res?.data?.results || []))
        .catch(() => {});

      // Auto-show progress view if onboarding is substantially complete
      if (tlRes?.data?.progress >= 70) {
        setSubmitted(true);
      }
    }).finally(() => setLoading(false));
  }, [clientSlug]);

  const handleSave = useCallback(async (isSubmission = false) => {
    setSaving(true);
    try {
      const payload = {
        basics: form.basics,
        secondary_pocs: form.secondaryPocs,
        billing_contact: form.billingContact,
        billing_acknowledged: form.billingAcknowledged,
        assets_folder_link: form.assets_folder_link,
        use_stock_assets: form.use_stock_assets,
        sales_schedule: form.salesSchedule,
        evergreen_offer: form.evergreenOffer,
        financing_offer: form.financingOffer,
        offers: form.offers || {},
        crm_choice: form.crm.choice,
        crm_other: form.crm.other,
        crm_wants_google_sheet: form.crm.wantsGoogleSheet,
        crm_email_notifications: form.crm.emailNotifications,
        crm_notification_emails: form.crm.notificationEmails,
        crm_posting_notes: form.crm.postingNotes,
        launch_goal: form.launchDate ? form.launchDate.format('YYYY-MM-DD') : null,
        bath_manufacturers: form.bathManufacturers,
      };
      if (isSubmission) payload._submitted = true;
      await saveOnboarding(clientSlug, payload);

      // Create email notification configs from comma-separated emails
      if (form.crm.emailNotifications && form.crm.notificationEmails) {
        try {
          const emails = form.crm.notificationEmails.split(',').map((e) => e.trim()).filter(Boolean);
          for (const email of emails) {
            await defaultClient.request({
              url: `app1/email-notifications/${clientSlug}/`,
              method: 'POST',
              payload: {
                email,
                name: '',
                notify_standard_leads: true,
                notify_self_book_leads: true,
                conditions: [],
              },
            }).catch(() => {}); // Ignore duplicates
          }
        } catch {
          // Non-critical — don't fail the save
        }
      }

      toast.success('Progress saved!');
      // Refresh timeline
      const tlRes = await fetchTimeline(clientSlug).catch(() => null);
      if (tlRes?.data) {
        setTimeline(tlRes.data);
        setLandingPages(tlRes.data.landing_pages || []);
      }
    } catch {
      toast.error('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [clientSlug, form]);

  const handleNext = async () => {
    await handleSave();
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));

  const handleInviteMember = async () => {
    if (!inviteEmail) { toast.error('Email is required'); return; }
    setInviting(true);
    try {
      const [firstName, ...rest] = (inviteName || '').split(' ');
      await defaultClient.request({
        url: 'accounts/auth/client-memberships/invite/',
        method: 'POST',
        payload: {
          email: inviteEmail,
          first_name: firstName || '',
          last_name: rest.join(' ') || '',
          role: 'editor',
          client_id: client?.id || undefined,
          client_slug: clientSlug,
        },
      });
      toast.success('Invite sent!');
      setInviteOpen(false);
      setInviteEmail('');
      setInviteName('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const handleLpAction = async (lpId, action, feedback = '') => {
    setLpSubmitting(true);
    try {
      await defaultClient.request({
        url: `clients/${clientSlug}/onboarding/landing-page/${lpId}/approve/`,
        method: 'POST',
        payload: { action, feedback },
      });
      toast.success(action === 'approve' ? 'Landing page approved!' : 'Feedback submitted!');
      setLpFeedbackOpen(null);
      setLpFeedbackText('');
      // Refresh timeline to update LP state
      const tlRes = await fetchTimeline(clientSlug).catch(() => null);
      if (tlRes?.data) {
        setTimeline(tlRes.data);
        setLandingPages(tlRes.data.landing_pages || []);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally {
      setLpSubmitting(false);
    }
  };

  const handleReviewAction = async (batchId, action) => {
    setReviewSubmitting(true);
    try {
      const payload = { action };
      if (action === 'approve') payload.name = reviewName;
      if (action === 'request_changes') payload.feedback = reviewFeedback;
      await defaultClient.request({
        url: `app1/creative-review/${clientSlug}/${batchId}/action/`,
        method: 'POST',
        payload,
      });
      toast.success(action === 'approve' ? 'Creative approved!' : 'Feedback submitted!');
      setReviewAction(null);
      setReviewFeedback('');
      setReviewName('');
      const res = await defaultClient.request({ url: `app1/creative-review/${clientSlug}/` });
      setCreativeReviews(res?.data?.results || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const updateBasics = (field) => (e) => setForm((prev) => ({ ...prev, basics: { ...prev.basics, [field]: e.target.value } }));
  const updateBilling = (field) => (e) => setForm((prev) => ({ ...prev, billingContact: { ...prev.billingContact, [field]: e.target.value } }));
  const updateCrm = (field) => (val) => setForm((prev) => ({ ...prev, crm: { ...prev.crm, [field]: typeof val === 'object' && val.target ? val.target.value : val } }));

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  const clientName = client?.dba_name || client?.name || clientSlug;
  const completionSteps = (timeline?.steps || []).filter((s) => s.key !== 'approvals');
  const overallProgress = timeline?.progress || 0;

  if (submitted) {
    const guruProgress = timeline?.guru_progress || [];
    const clientActions = timeline?.client_actions || [];
    const estimatedLaunch = timeline?.estimated_launch;
    const launchReason = timeline?.estimated_launch_reason || '';
    const isLive = launchReason === 'live';

    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        <Helmet><title>Onboarding Complete | {config.SITE.title}</title></Helmet>
        <Box sx={{ bgcolor: '#111827', py: 2, px: 3 }}>
          <Container maxWidth="lg">
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <img src={leadGurusLogo} alt="Lead Gurus" style={{ height: 28 }} />
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" sx={{ color: '#9ca3af' }}>{authState?.user?.email || clientName}</Typography>
                <Button size="small" startIcon={<LogoutIcon />} onClick={logout}
                  sx={{ color: '#9ca3af', textTransform: 'none', fontSize: 12, '&:hover': { color: '#fff' } }}>
                  Logout
                </Button>
              </Stack>
            </Stack>
          </Container>
        </Box>
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" fontWeight={700} gutterBottom>Onboarding Submitted!</Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', mb: 2 }}>
              Thank you for completing your onboarding for <strong>{clientName}</strong>. Our team has been notified and is getting started on your campaigns.
            </Typography>
            <Stack direction="row" spacing={1.5} justifyContent="center">
              <Button
                variant="outlined" size="small"
                onClick={() => setSubmitted(false)}
              >
                Edit Onboarding
              </Button>
              <Button
                variant="outlined" size="small" startIcon={<PersonAddIcon />}
                onClick={() => { setInviteEmail(''); setInviteName(''); setInviteOpen(true); }}
              >
                Invite Team Member
              </Button>
              <Button
                variant="outlined" size="small"
                href={`/projections/${clientSlug}/client-view`}
                target="_blank"
              >
                View Projections
              </Button>
              <Button
                variant="outlined" size="small"
                href={`/clients/${clientSlug}/webhook-setup`}
                target="_blank"
              >
                Lead Delivery
              </Button>
            </Stack>
          </Box>

          {/* Invite member dialog */}
          <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Invite a team member to access the portal. They can help complete onboarding, review creative, configure lead delivery, and more.
                </Typography>
                <TextField
                  label="Their Name" value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  fullWidth size="small" placeholder="John Smith"
                />
                <TextField
                  label="Their Email" type="email" value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  fullWidth size="small" required placeholder="john@yourcompany.com"
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
              <Button
                onClick={handleInviteMember} variant="contained"
                disabled={inviting || !inviteEmail}
              >
                {inviting ? 'Sending...' : 'Send Invite'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Horizontal build timeline */}
          {guruProgress.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ py: 2.5, px: 3 }}>
                <Stepper
                  activeStep={guruProgress.findIndex((p) => p.progress > 0 && p.progress < 100)}
                  alternativeLabel
                  sx={{
                    '& .MuiStepConnector-line': { borderTopWidth: 3 },
                    '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': { borderColor: '#16a34a' },
                    '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': { borderColor: '#ff680d' },
                  }}
                >
                  {guruProgress.map((phase) => {
                    const done = phase.progress === 100;
                    const active = phase.progress > 0 && phase.progress < 100;
                    return (
                      <Step key={phase.phase} completed={done}>
                        <StepButton
                          icon={
                            done
                              ? <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 28 }} />
                              : active
                                ? <CircularProgress variant="determinate" value={phase.progress} size={24} thickness={5} sx={{ color: '#ff680d' }} />
                                : <RadioButtonUncheckedIcon sx={{ color: '#ccc', fontSize: 24 }} />
                          }
                          sx={{ cursor: 'default' }}
                        >
                          <Typography variant="caption" fontWeight={done || active ? 700 : 400}
                            sx={{ color: done ? '#16a34a' : active ? '#ff680d' : 'text.secondary', fontSize: 11 }}>
                            {phase.phase}
                          </Typography>
                          {active && (
                            <Typography variant="caption" sx={{ color: '#ff680d', fontSize: 10, display: 'block' }}>
                              {phase.complete}/{phase.total}
                            </Typography>
                          )}
                        </StepButton>
                      </Step>
                    );
                  })}
                </Stepper>
              </CardContent>
            </Card>
          )}

          <Grid container spacing={4}>
            <Grid item xs={12} md={8} sx={{ mx: 'auto' }}>
              {isLive ? (
                <Card sx={{ mb: 3, border: '2px solid #16a34a', bgcolor: '#f0fdf4' }}>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="overline" color="text.secondary">Campaign Status</Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#16a34a' }}>Live!</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Your campaigns are running and leads are flowing.
                    </Typography>
                  </CardContent>
                </Card>
              ) : estimatedLaunch ? (
                <Card sx={{ mb: 3, border: '2px solid #ff680d', bgcolor: '#fff7ed' }}>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="overline" color="text.secondary">Estimated Launch Date</Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#ff680d' }}>
                      {new Date(estimatedLaunch + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {launchReason}
                    </Typography>
                    {launchReason.includes('assets') && (
                      <Button
                        variant="contained" size="small" sx={{ mt: 2, bgcolor: '#ff680d', '&:hover': { bgcolor: '#ea580c' } }}
                        onClick={() => { setSubmitted(false); setActiveStep(2); }}
                        startIcon={<UploadFileIcon />}
                      >
                        Upload Assets Now
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : null}

              {/* ===== YOUR TO-DOS ===== */}
              {(clientActions.length > 0 || creativeReviews.length > 0 || landingPages.length > 0) && (
                <Card sx={{ mb: 3, border: '2px solid #ff680d' }}>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Box sx={{
                        bgcolor: '#ff680d', color: '#fff', px: 1.5, py: 0.5, borderRadius: 1,
                        fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5,
                      }}>
                        Action Required
                      </Box>
                      <Typography variant="h6" fontWeight={700}>Your To-Dos</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                      Complete these items so we can finalize your campaigns and start sending you leads.
                    </Typography>

                    {/* Checklist-style action items */}
                    {clientActions.length > 0 && (
                      <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                        {clientActions.map((action) => (
                          <Stack key={action.key} direction="row" spacing={1.5} alignItems="center"
                            sx={{
                              p: 1.5, borderRadius: 1,
                              cursor: action.link && (!action.completed || action.key === 'lead_delivery') ? 'pointer' : 'default',
                              bgcolor: action.completed ? 'success.50' : 'warning.50',
                              '&:hover': action.link && (!action.completed || action.key === 'lead_delivery') ? { bgcolor: action.completed ? 'success.100' : 'warning.100' } : {},
                            }}
                            onClick={() => {
                              if (action.link && action.key === 'lead_delivery') {
                                window.open(action.link, '_blank');
                              } else if (action.link && !action.completed) {
                                window.open(action.link, '_blank');
                              }
                            }}
                          >
                            {action.completed ? (
                              <CheckCircleIcon sx={{ color: 'success.main' }} />
                            ) : (
                              <RadioButtonUncheckedIcon sx={{ color: 'warning.main' }} />
                            )}
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={600}>{action.label}</Typography>
                              <Typography variant="caption" color="text.secondary">{action.description}</Typography>
                            </Box>
                            {action.key === 'lead_delivery' && action.completed && (
                              <Button size="small" variant="outlined" color="success" onClick={(e) => { e.stopPropagation(); window.open(action.link, '_blank'); }}>Edit</Button>
                            )}
                            {action.key === 'lead_delivery' && !action.completed && (
                              <Stack direction="row" spacing={1}>
                                {!action.has_url && (
                                  <Button size="small" variant="outlined" color="warning" onClick={() => window.open(action.link, '_blank')}>Setup</Button>
                                )}
                                {action.has_url && !action.tested && (
                                  <Button size="small" variant="outlined" color="warning" onClick={() => window.open(action.link, '_blank')}>Send Test</Button>
                                )}
                                {action.tested && !action.completed && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        await defaultClient.request({
                                          url: `clients/${clientSlug}/onboarding/confirm-lead-delivery/`,
                                          method: 'POST',
                                        });
                                        toast.success('Lead delivery confirmed!');
                                        const tlRes = await fetchTimeline(clientSlug).catch(() => null);
                                        if (tlRes?.data) setTimeline(tlRes.data);
                                      } catch (err) {
                                        toast.error(err?.response?.data?.detail || 'Failed to confirm');
                                      }
                                    }}
                                  >
                                    Confirm Received
                                  </Button>
                                )}
                              </Stack>
                            )}
                            {action.key !== 'lead_delivery' && !action.completed && action.link && (
                              <Button size="small" variant="outlined" color="warning">Setup</Button>
                            )}
                          </Stack>
                        ))}
                      </Stack>
                    )}

                    {/* Expert context note */}
                    {(creativeReviews.length > 0 || landingPages.length > 0) && (
                      <>
                        <Divider sx={{ mb: 2.5 }} />
                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', mb: 2.5 }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Before You Review</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Our approach is shaped by hundreds of millions in home-improvement ad spend, and everything here is built on what's working best right now. These concepts follow a proven framework designed to drive performance from the start.
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            The primary goal of these ads and landing pages is <strong>lead generation</strong> — branding is secondary. The creative is built to get responses first, with your brand feel layered in.
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Your input matters most on messaging and offer accuracy.</strong> The style, structure, and editing follow a system that consistently delivers results. We include one round of revisions, so please consolidate all notes into a single pass.
                          </Typography>
                        </Box>
                      </>
                    )}

                    {/* Ad Creative Review */}
                    {creativeReviews.length > 0 && (
                      <>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle1" fontWeight={700}>Ad Creative Review</Typography>
                            <Chip label="Your Review Needed" size="small" color="warning" variant="filled" sx={{ fontSize: 11, height: 22 }} />
                          </Stack>
                          <Stack direction="row" spacing={0.75}>
                            {(() => {
                              const total = creativeReviews.reduce((s, b) => s + (b.assets?.length || 0), 0);
                              const approved = creativeReviews.reduce((s, b) => s + (b.assets || []).filter((a) => a.asset_status === 'approved').length, 0);
                              const changes = creativeReviews.reduce((s, b) => s + (b.assets || []).filter((a) => a.asset_status === 'changes_requested').length, 0);
                              const pending = total - approved - changes;
                              return (
                                <>
                                  {approved > 0 && <Chip label={`${approved} Approved`} size="small" color="success" />}
                                  {changes > 0 && <Chip label={`${changes} Changes`} size="small" color="warning" />}
                                  {pending > 0 && <Chip label={`${pending} Pending`} size="small" color="info" />}
                                </>
                              );
                            })()}
                          </Stack>
                        </Stack>
                        <Stack spacing={3}>
                          {(() => {
                            const allAssets = creativeReviews.flatMap((b) =>
                              (b.assets || []).map((a) => ({ ...a, batch: b }))
                            );
                            if (!allAssets.length) return null;
                            return (
                              <CreativeCarousel
                                assets={allAssets}
                                clientSlug={clientSlug}
                                onRefresh={() => {
                                  defaultClient.request({ url: `app1/creative-review/${clientSlug}/` })
                                    .then((res) => setCreativeReviews(res?.data?.results || []))
                                    .catch(() => {});
                                }}
                              />
                            );
                          })()}
                        </Stack>
                      </>
                    )}

                    {/* Landing Page Approval */}
                    {landingPages.length > 0 && (
                      <>
                        <Divider sx={{ my: 2.5 }} />
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight={700}>Landing Page Approval</Typography>
                          {landingPages.some((lp) => !lp.approved) ? (
                            <Chip label="Your Approval Needed" size="small" color="warning" variant="filled" sx={{ fontSize: 11, height: 22 }} />
                          ) : (
                            <Chip label="All Approved" size="small" color="success" variant="filled" sx={{ fontSize: 11, height: 22 }} />
                          )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Preview each landing page and approve before we go live. Click the link to open in a new tab.
                        </Typography>
                        <Stack spacing={2}>
                          {landingPages.map((lp) => (
                            <LandingPageCard
                              key={lp.id}
                              lp={lp}
                              submitting={lpSubmitting}
                              onAction={(action, text) => handleLpAction(lp.id, action, text)}
                            />
                          ))}
                        </Stack>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Approval dialog */}
              <Dialog open={!!reviewAction} onClose={() => setReviewAction(null)} maxWidth="xs" fullWidth>
                <DialogTitle>
                  {reviewAction?.action === 'approve' ? 'Approve Creative' : 'Request Changes'}
                  {reviewAction?.assetLabel && (
                    <Typography variant="body2" color="text.secondary">{reviewAction.assetLabel}</Typography>
                  )}
                </DialogTitle>
                <DialogContent>
                  {reviewAction?.action === 'approve' ? (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                      <Typography variant="body2">By approving, you authorize us to use this creative in your ad campaigns.</Typography>
                    </Stack>
                  ) : (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                      <Typography variant="body2">Describe what changes you'd like. One round of revisions is included.</Typography>
                      <TextField
                        label="Feedback" value={reviewFeedback}
                        onChange={(e) => setReviewFeedback(e.target.value)}
                        fullWidth multiline minRows={3}
                        placeholder="e.g., Please change the headline to..., The image should show..."
                      />
                    </Stack>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setReviewAction(null)}>Cancel</Button>
                  <Button
                    variant="contained"
                    onClick={() => handleReviewAction(reviewAction.batchId, reviewAction.action)}
                    disabled={reviewSubmitting || (reviewAction?.action === 'request_changes' && !reviewFeedback)}
                  >
                    {reviewSubmitting ? 'Submitting...' : reviewAction?.action === 'approve' ? 'Approve' : 'Submit Feedback'}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Activity Feed */}
              {(timeline?.activity || []).length > 0 && (
                <Card sx={{ mt: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Recent Activity</Typography>
                    <Stack spacing={0}>
                      {(timeline.activity || []).map((item, idx) => {
                        const iconMap = {
                          contract: '📝',
                          creative: '🎨',
                          landing_page: '🌐',
                          webhook: '🔗',
                          asset: '📁',
                          phase: '✅',
                        };
                        const dateStr = new Date(item.date).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric',
                        });
                        const timeStr = new Date(item.date).toLocaleTimeString('en-US', {
                          hour: 'numeric', minute: '2-digit',
                        });
                        return (
                          <Stack key={idx} direction="row" spacing={1.5} alignItems="flex-start"
                            sx={{
                              py: 1.5,
                              borderBottom: idx < (timeline.activity.length - 1) ? '1px solid' : 'none',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography sx={{ fontSize: 18, lineHeight: 1.4, minWidth: 28, textAlign: 'center' }}>
                              {iconMap[item.icon] || '📌'}
                            </Typography>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2">{item.text}</Typography>
                              <Typography variant="caption" color="text.secondary">{dateStr} at {timeStr}</Typography>
                            </Box>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </CardContent>
                </Card>
              )}

            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet><title>Onboarding | {clientName} | {config.SITE.title}</title></Helmet>

      {/* Header */}
      <Box sx={{ bgcolor: '#111827', py: 2, px: 3 }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <img src={leadGurusLogo} alt="Lead Gurus" style={{ height: 28 }} />
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ color: '#9ca3af' }}>{authState?.user?.email || clientName}</Typography>
              <Button size="small" startIcon={<LogoutIcon />} onClick={logout}
                sx={{ color: '#9ca3af', textTransform: 'none', fontSize: 12, '&:hover': { color: '#fff' } }}>
                Logout
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Left: Form */}
          <Grid item xs={12} md={8}>
            <Typography variant="h4" fontWeight={700} gutterBottom>Client Onboarding</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Complete each section to help us launch your campaigns. Your progress saves automatically.
            </Typography>

            {/* Progress bar */}
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>Overall Progress</Typography>
                <Typography variant="body2" color="text.secondary">{overallProgress}%</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={overallProgress} sx={{ height: 8, borderRadius: 4 }} />
            </Box>

            {/* Stepper */}
            <Stepper activeStep={activeStep} nonLinear sx={{ mb: 3, flexWrap: 'wrap', rowGap: 1 }}>
              {STEPS.map((step, idx) => {
                const completed = completionSteps.find((s) => s.key === step.key)?.completed;
                return (
                  <Step key={step.key} completed={!!completed}>
                    <StepButton onClick={() => setActiveStep(idx)}>
                      {step.label}
                    </StepButton>
                  </Step>
                );
              })}
            </Stepper>

            {/* Step content */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                {activeStep === 0 && (
                  <StepBasics form={form} updateBasics={updateBasics} setForm={setForm} />
                )}
                {activeStep === 1 && (
                  <StepBilling form={form} updateBilling={updateBilling} setForm={setForm} clientSlug={clientSlug} />
                )}
                {activeStep === 2 && (
                  <StepBudgets clientId={client?.id} onConfirmed={async () => {
                    const tlRes = await fetchTimeline(clientSlug).catch(() => null);
                    if (tlRes?.data) {
                      setTimeline(tlRes.data);
                      setLandingPages(tlRes.data.landing_pages || []);
                    }
                  }} />
                )}
                {activeStep === 3 && (
                  <StepAssets form={form} setForm={setForm} />
                )}
                {activeStep === 4 && (
                  <StepOffers form={form} setForm={setForm} client={client}
                    offerOptions={offerOptions} clientVerticals={clientVerticals} />
                )}
                {activeStep === 5 && (
                  <StepCrm form={form} updateCrm={updateCrm} setForm={setForm} clientSlug={clientSlug} clientId={client?.id} />
                )}
                {activeStep === 6 && (
                  <StepLaunch form={form} setForm={setForm} />
                )}
                {activeStep === 7 && (
                  <StepReview completionSteps={completionSteps} clientName={clientName} progress={overallProgress} onGoToStep={setActiveStep} />
                )}
              </CardContent>
            </Card>

            {/* Navigation */}
            <Stack direction="row" justifyContent="space-between">
              <Button onClick={handleBack} disabled={activeStep === 0} startIcon={<NavigateBeforeIcon />} variant="outlined">
                Back
              </Button>
              {activeStep < STEPS.length - 1 ? (
                <Button onClick={handleNext} endIcon={saving ? <CircularProgress size={16} /> : <NavigateNextIcon />} variant="contained" disabled={saving}>
                  Next
                </Button>
              ) : (
                <Button onClick={async () => { await handleSave(true); setSubmitted(true); }} variant="contained" disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                  sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}>
                  Submit Onboarding
                </Button>
              )}
            </Stack>
          </Grid>

          {/* Right: Timeline sidebar */}
          <Grid item xs={12} md={4}>
            <Card sx={{ position: 'sticky', top: 80 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>Your Progress</Typography>
                <Stack spacing={1.5}>
                  {completionSteps.map((step) => (
                    <Stack key={step.key} direction="row" spacing={1.5} alignItems="center">
                      {step.completed ? (
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                      ) : (
                        <RadioButtonUncheckedIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                      )}
                      <Box>
                        <Typography variant="body2" fontWeight={step.completed ? 600 : 400}
                          color={step.completed ? 'success.main' : 'text.primary'}>
                          {step.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{step.description}</Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>

                <Divider sx={{ my: 2 }} />
                <Button
                  size="small" variant="outlined" fullWidth
                  href={`/projections/${clientSlug}/client-view`}
                  target="_blank"
                  sx={{ textTransform: 'none' }}
                >
                  View Your Projections
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Step Components
// ---------------------------------------------------------------------------

function StepBasics({ form, updateBasics, setForm }) {
  return (
    <Stack spacing={3}>
      <Typography variant="h6">Contact Information</Typography>
      <Typography variant="body2" color="text.secondary">Who is the main point of contact for this account?</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}><TextField label="First Name" value={form.basics.firstName} onChange={updateBasics('firstName')} fullWidth required /></Grid>
        <Grid item xs={12} sm={6}><TextField label="Last Name" value={form.basics.lastName} onChange={updateBasics('lastName')} fullWidth required /></Grid>
        <Grid item xs={12} sm={6}><TextField label="Email" type="email" value={form.basics.email} onChange={updateBasics('email')} fullWidth required /></Grid>
        <Grid item xs={12} sm={6}><TextField label="Position / Title" value={form.basics.position} onChange={updateBasics('position')} fullWidth /></Grid>
      </Grid>
    </Stack>
  );
}

function StepBilling({ form, updateBilling, setForm, clientSlug }) {
  const [cardStatus, setCardStatus] = useState(null); // null = loading, { has_card, card }
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [cardSaved, setCardSaved] = useState(false);

  // Check if card is already on file + get publishable key
  useEffect(() => {
    if (!clientSlug) return;
    defaultClient.request({ url: `app1/setup-intent/${clientSlug}/` })
      .then((res) => {
        const data = res?.data || {};
        setCardStatus(data);
        if (data.has_card) setCardSaved(true);
        if (data.publishable_key) {
          setStripePromise((prev) => prev || loadStripe(data.publishable_key));
        }
      })
      .catch(() => setCardStatus({ has_card: false }));
  }, [clientSlug]);

  // Create SetupIntent when user is ready to add card
  const handleSetupCard = async () => {
    try {
      const res = await defaultClient.request({
        url: `app1/setup-intent/${clientSlug}/`,
        method: 'POST',
      });
      setClientSecret(res?.data?.client_secret);
      if (res?.data?.publishable_key) {
        setStripePromise((prev) => prev || loadStripe(res.data.publishable_key));
      }
    } catch (err) {
      console.error('Card setup error:', err?.response?.status, err?.response?.data);
      toast.error(err?.response?.data?.detail || 'Failed to initialize card setup. Please try again.');
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h6">Billing Information</Typography>
      <Typography variant="body2" color="text.secondary">Who should receive invoices and billing communications?</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}><TextField label="Billing Contact Name" value={form.billingContact.name} onChange={updateBilling('name')} fullWidth required /></Grid>
        <Grid item xs={12} sm={6}><TextField label="Billing Email" type="email" value={form.billingContact.email} onChange={updateBilling('email')} fullWidth required /></Grid>
        <Grid item xs={12} sm={6}><TextField label="Phone" value={form.billingContact.phone} onChange={updateBilling('phone')} fullWidth /></Grid>
        <Grid item xs={12}><TextField label="Address Line 1" value={form.billingContact.addressLine1} onChange={updateBilling('addressLine1')} fullWidth /></Grid>
        <Grid item xs={12}><TextField label="Address Line 2" value={form.billingContact.addressLine2} onChange={updateBilling('addressLine2')} fullWidth /></Grid>
        <Grid item xs={12} sm={4}><TextField label="City" value={form.billingContact.city} onChange={updateBilling('city')} fullWidth /></Grid>
        <Grid item xs={12} sm={4}><TextField label="State" value={form.billingContact.state} onChange={updateBilling('state')} fullWidth /></Grid>
        <Grid item xs={12} sm={4}><TextField label="ZIP Code" value={form.billingContact.postalCode} onChange={updateBilling('postalCode')} fullWidth /></Grid>
      </Grid>

      <Divider />

      {/* Card on file section */}
      <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Payment Method</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A credit card on file is required before we launch your campaigns. This card will only be charged per the terms of your signed agreement.
        </Typography>

        {cardSaved ? (
          <Alert severity="success" variant="outlined" icon={<CheckCircleIcon />}>
            <strong>Card on file:</strong>{' '}
            {cardStatus?.card ? (
              <>{cardStatus.card.brand?.toUpperCase()} ending in {cardStatus.card.last4} (exp {cardStatus.card.exp_month}/{cardStatus.card.exp_year})</>
            ) : (
              'Payment method saved'
            )}
          </Alert>
        ) : clientSecret && stripePromise ? (
          <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
            <CardCollectionForm
              clientSecret={clientSecret}
              onSuccess={(card) => {
                setCardSaved(true);
                setCardStatus((prev) => ({ ...prev, has_card: true, card }));
                setClientSecret(null);
                // Auto-save to refresh billing completion
                handleSave();
              }}
            />
          </Elements>
        ) : (
          <Button
            variant="outlined" onClick={handleSetupCard}
            disabled={!form.billingContact.name || !form.billingContact.email}
            sx={{ textTransform: 'none' }}
          >
            Add Credit Card
          </Button>
        )}

        {!cardSaved && !clientSecret && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {(!form.billingContact.name || !form.billingContact.email)
              ? 'Fill in the billing contact name and email above first.'
              : 'Click to securely add a card. No charges will be made now.'}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function CardCollectionForm({ clientSecret, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSaving(true);
    setError(null);

    const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
      clientSecret,
      { payment_method: { card: elements.getElement(CardElement) } }
    );

    if (stripeError) {
      setError(stripeError.message);
      setSaving(false);
      return;
    }

    if (setupIntent?.status === 'succeeded') {
      // Get card details from the payment method
      try {
        const pm = await stripe.retrievePaymentMethod(setupIntent.payment_method);
        const card = pm?.paymentMethod?.card || {};
        onSuccess({
          brand: card.brand || '',
          last4: card.last4 || '',
          exp_month: card.exp_month,
          exp_year: card.exp_year,
        });
        toast.success('Card saved successfully!');
      } catch {
        onSuccess(null);
        toast.success('Card saved!');
      }
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{
        border: '1px solid #e0e0e0', borderRadius: 2, p: 2, bgcolor: '#fafafa',
      }}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#333',
                fontFamily: 'Arial, sans-serif',
                '::placeholder': { color: '#999' },
              },
              invalid: { color: '#dc2626' },
            },
          }}
        />
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>{error}</Typography>
      )}
      <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
        <Button type="submit" variant="contained" disabled={!stripe || saving}
          sx={{ bgcolor: '#ff680d', '&:hover': { bgcolor: '#ea580c' } }}>
          {saving ? 'Saving...' : 'Save Card'}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
          Secured by Stripe. No charges will be made now.
        </Typography>
      </Stack>
    </form>
  );
}

function StepBudgets({ clientId, onConfirmed }) {
  const [segments, setSegments] = useState([]);
  const [verticalOptions, setVerticalOptions] = useState([]);
  const [territoryOptions, setTerritoryOptions] = useState([]);
  const [pageStatus, setPageStatus] = useState('draft');
  const [loading, setLoading] = useState(true);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Always work in the current month — onboarding is upfront, not month-specific.
  const period = useMemo(() => dayjs().startOf('month').format('YYYY-MM-DD'), []);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [bcRes, vRes, tRes] = await Promise.all([
        defaultClient.request({
          url: `dashboard/client-budgets/budget-center/?client_id=${clientId}&period=${period}`,
        }),
        defaultClient.request({
          url: `dashboard/client-budgets/budget-verticals/?client_id=${clientId}`,
        }).catch(() => ({ data: [] })),
        defaultClient.request({
          url: `dashboard/client-territories/?client_id=${clientId}`,
        }).catch(() => ({ data: [] })),
      ]);
      const data = bcRes?.data || {};
      const segs = Array.isArray(data.segments) ? data.segments : [];
      setSegments(segs.map((s) => ({
        budget_id: s.budget_id ?? null,
        source_budget_id: s.source_budget_id ?? s.budget_id ?? null,
        vertical: s.vertical ?? s.vertical_data?.id ?? '',
        location: s.location ?? s.location_data?.id ?? '',
        amount: String(s.amount ?? ''),
      })));
      setPageStatus(data.status || 'draft');
      const vRows = Array.isArray(vRes?.data) ? vRes.data : (vRes?.data?.results || []);
      setVerticalOptions(vRows.map((r) => ({ value: r.id, label: r.vertical })));
      const tRows = Array.isArray(tRes?.data) ? tRes.data : (tRes?.data?.results || []);
      setTerritoryOptions(tRows.map((r) => ({ value: r.id, label: r.name })));
    } catch (err) {
      toast.error('Failed to load budgets');
    } finally {
      setLoading(false);
    }
  }, [clientId, period]);

  useEffect(() => { load(); }, [load]);

  const updateSegment = (idx, field, value) => {
    setSegments((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const addSegment = () => {
    setSegments((prev) => [
      ...prev,
      { budget_id: null, source_budget_id: null, vertical: '', location: '', amount: '' },
    ]);
  };

  const removeSegment = (idx) => {
    setSegments((prev) => prev.filter((_, i) => i !== idx));
  };

  const buildPayload = () => ({
    client_id: clientId,
    period,
    allocation_mode: 'optimize_freely',
    allocation_config: {},
    segments: segments
      .filter((s) => s.amount && Number(s.amount) > 0)
      .map((s) => ({
        budget_id: s.budget_id || undefined,
        source_budget_id: s.source_budget_id || undefined,
        vertical: s.vertical || null,
        location: s.location || null,
        channel: null,
        amount: Number(s.amount),
      })),
  });

  const validate = (payload) => {
    if (!payload.segments.length) {
      toast.error('Add at least one segment with an amount');
      return false;
    }
    const seen = new Set();
    for (const s of payload.segments) {
      const key = `${s.vertical || ''}|${s.location || ''}`;
      if (seen.has(key)) {
        toast.error('Two segments share the same vertical and territory — combine them');
        return false;
      }
      seen.add(key);
    }
    return true;
  };

  const handleSaveDraft = async () => {
    const payload = buildPayload();
    if (!validate(payload)) return;
    setSavingDraft(true);
    try {
      await defaultClient.request({
        url: 'dashboard/client-budgets/budget-center/save-draft/',
        method: 'POST',
        payload,
      });
      toast.success('Draft saved');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleConfirm = async () => {
    const payload = buildPayload();
    if (!validate(payload)) return;
    setSubmitting(true);
    try {
      await defaultClient.request({
        url: 'dashboard/client-budgets/budget-center/approve/',
        method: 'POST',
        payload,
      });
      toast.success('Budgets confirmed!');
      await load();
      if (onConfirmed) await onConfirmed();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to confirm budgets');
    } finally {
      setSubmitting(false);
    }
  };

  const total = segments.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
  const isConfirmed = ['pending_approval', 'approved', 'active'].includes(pageStatus);

  if (!clientId) {
    return <Alert severity="warning">Loading client info…</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h6">Confirm Your Budgets</Typography>
      <Typography variant="body2" color="text.secondary">
        Set your monthly investment by vertical (and optionally by territory). You can add a segment for each combination you want to fund. Confirm when you're ready and your account manager will finalize before launch.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          {isConfirmed && (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              Budgets submitted for review. You can still edit and re-submit until your account manager publishes them.
            </Alert>
          )}

          <Stack spacing={1.5}>
            {segments.length === 0 && (
              <Alert severity="info" variant="outlined">
                No segments yet. Add one to set your monthly budget.
              </Alert>
            )}
            {segments.map((s, idx) => (
              <Stack
                key={idx}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ sm: 'flex-start' }}
                sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <TextField
                  select
                  label="Vertical"
                  size="small"
                  value={s.vertical || ''}
                  onChange={(e) => updateSegment(idx, 'vertical', e.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">All verticals</MenuItem>
                  {verticalOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </TextField>
                {territoryOptions.length > 0 && (
                  <TextField
                    select
                    label="Territory (optional)"
                    size="small"
                    value={s.location || ''}
                    onChange={(e) => updateSegment(idx, 'location', e.target.value)}
                    sx={{ minWidth: 180 }}
                  >
                    <MenuItem value="">All territories</MenuItem>
                    {territoryOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField
                  label="Monthly Amount"
                  type="number"
                  size="small"
                  value={s.amount}
                  onChange={(e) => updateSegment(idx, 'amount', e.target.value)}
                  InputProps={{
                    startAdornment: <Box sx={{ mr: 0.5, color: 'text.secondary' }}>$</Box>,
                  }}
                  sx={{ minWidth: 160, flex: 1 }}
                />
                <IconButton onClick={() => removeSegment(idx)} aria-label="remove segment">
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}
          </Stack>

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addSegment}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add Segment
          </Button>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Total monthly budget
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              ${total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
            <Button
              variant="outlined"
              onClick={handleSaveDraft}
              disabled={savingDraft || submitting}
              startIcon={savingDraft ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              {savingDraft ? 'Saving…' : 'Save Draft'}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={submitting || savingDraft || segments.length === 0}
              startIcon={submitting ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            >
              {submitting ? 'Confirming…' : 'Confirm Budgets'}
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
}

function StepAssets({ form, setForm }) {
  const useStock = form.use_stock_assets || false;
  return (
    <Stack spacing={3}>
      <Typography variant="h6">Creative Assets</Typography>
      <Typography variant="body2" color="text.secondary">
        Share your photos, videos, and brand materials so we can build high-performing ads and landing pages.
      </Typography>
      {!useStock && (
        <TextField
          label="Google Drive / Dropbox Link"
          placeholder="https://drive.google.com/..."
          value={form.assets_folder_link}
          onChange={(e) => setForm((prev) => ({ ...prev, assets_folder_link: e.target.value }))}
          fullWidth
          helperText="Share a folder link containing your assets (see checklist below)"
        />
      )}
      {!useStock && (
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: '#ff680d', px: 2, py: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#fff' }}>Asset Checklist</Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Videos (most important for ad performance)</Typography>
            <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5, '& li': { fontSize: 13, color: '#333' } }}>
              <li>Job site / project walkthrough videos</li>
              <li>Before &amp; after transformation videos</li>
              <li>Team introductions or testimonial videos</li>
              <li>Drone footage of completed work</li>
              <li>Any existing video ads or social media content</li>
            </Stack>
            <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>Photos</Typography>
            <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5, '& li': { fontSize: 13, color: '#333' } }}>
              <li>Before &amp; after project photos (high resolution)</li>
              <li>Team / crew photos</li>
              <li>Branded truck, uniforms, signage</li>
              <li>Showroom or office photos</li>
            </Stack>
            <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>Brand Materials</Typography>
            <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5, '& li': { fontSize: 13, color: '#333' } }}>
              <li>Company logo (PNG or vector preferred)</li>
              <li>Brand colors and fonts (if you have a brand guide)</li>
            </Stack>
          </Box>
          <Box sx={{ bgcolor: '#fff7ed', px: 2, py: 1.5, borderTop: '1px solid #e2e8f0' }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Video content drives the best ad performance.</strong> Even short phone-recorded clips of your work outperform stock footage. The more you provide, the more creative options our team has to test and optimize.
            </Typography>
          </Box>
        </Box>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={useStock}
            onChange={(e) => setForm((prev) => ({ ...prev, use_stock_assets: e.target.checked }))}
          />
        }
        label={
          <Box>
            <Typography variant="body2" fontWeight={500}>I don't have assets — use professional stock media</Typography>
            <Typography variant="caption" color="text.secondary">
              Our team will source high-quality stock photos and videos for your ads and landing pages. You can still share your logo if you have one.
            </Typography>
          </Box>
        }
      />
      {useStock && (
        <TextField
          label="Company Logo Link (optional)"
          placeholder="https://drive.google.com/... or your website URL"
          value={form.assets_folder_link}
          onChange={(e) => setForm((prev) => ({ ...prev, assets_folder_link: e.target.value }))}
          fullWidth
          helperText="If you have a logo, share a link. Otherwise we'll pull it from your website."
        />
      )}
    </Stack>
  );
}

const BATH_MANUFACTURERS = [
  { value: 'bci', label: 'BCI' },
  { value: 'jacuzzi', label: 'Jacuzzi' },
  { value: 'kohler', label: 'Kohler' },
  { value: 'samuel_mueller', label: 'Samuel Mueller' },
];

function StepOffers({ form, setForm, client, offerOptions, clientVerticals }) {
  const hasBath = (client?.vertical_tags || '').toLowerCase().includes('bath');
  const verticals = clientVerticals || [];
  const byVertical = offerOptions?.by_vertical || {};
  const fallbackAd = offerOptions?.fallback_ad || [];
  const fallbackFin = offerOptions?.fallback_financing || [];
  const offers = form.offers || {};

  const getAdOptions = (verticalId) => byVertical[verticalId]?.ad || fallbackAd;
  const getFinOptions = (verticalId) => byVertical[verticalId]?.financing || fallbackFin;

  const updateOffer = (verticalId, offerType, field, value) => {
    setForm((prev) => {
      const newOffers = { ...prev.offers };
      const key = `${verticalId}_${offerType}`;
      newOffers[key] = { ...(newOffers[key] || {}), vertical_id: verticalId, offer_type: offerType, [field]: value };
      if (field === 'selected_label') {
        newOffers[key].custom_label = '';
      }
      if (field === 'custom_label') {
        newOffers[key].selected_label = '';
      }
      // Evergreen offer mirrors to LP offer
      if (offerType === 'offer') {
        const lpKey = `${verticalId}_lp_offer`;
        newOffers[lpKey] = {
          ...(newOffers[lpKey] || {}),
          vertical_id: verticalId,
          offer_type: 'lp_offer',
          selected_label: newOffers[key].selected_label || '',
          custom_label: newOffers[key].custom_label || '',
        };
      }
      return { ...prev, offers: newOffers };
    });
  };

  const getOffer = (verticalId, offerType) => offers[`${verticalId}_${offerType}`] || {};

  const renderVerticalOffers = (verticalId) => {
    const evOffer = getOffer(verticalId, 'offer');
    const finOffer = getOffer(verticalId, 'financing');
    const evCustom = evOffer.custom_label || '';
    const evSelected = evOffer.selected_label || '';
    const finCustom = finOffer.custom_label || '';
    const finSelected = finOffer.selected_label || '';
    const adOpts = getAdOptions(verticalId);
    const finOpts = getFinOptions(verticalId);

    return (
      <>
        {/* Evergreen Offer */}
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Evergreen Offer *</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Select a suggested offer or type your own below</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {adOpts.map((opt) => (
              <Chip
                key={opt.label} label={opt.label}
                onClick={() => updateOffer(verticalId, 'offer', 'selected_label', evSelected === opt.label ? '' : opt.label)}
                color={evSelected === opt.label ? 'primary' : 'default'}
                variant={evSelected === opt.label ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Stack>
          <TextField
            size="small" fullWidth
            placeholder="Or type a custom offer (e.g., Free estimate, Buy one get one...)"
            value={evCustom}
            onChange={(e) => updateOffer(verticalId, 'offer', 'custom_label', e.target.value)}
          />
        </Box>

        {/* Financing Offer */}
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Financing Offer</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Select a financing option or type your own below</Typography>
          {finOpts.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              {finOpts.map((opt) => (
                <Chip
                  key={opt.label} label={opt.label}
                  onClick={() => updateOffer(verticalId, 'financing', 'selected_label', finSelected === opt.label ? '' : opt.label)}
                  color={finSelected === opt.label ? 'primary' : 'default'}
                  variant={finSelected === opt.label ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Stack>
          )}
          <TextField
            size="small" fullWidth
            placeholder="e.g., 0% for 24 months, $99/mo, No payments for 12 months..."
            value={finCustom}
            onChange={(e) => updateOffer(verticalId, 'financing', 'custom_label', e.target.value)}
          />
        </Box>
      </>
    );
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h6">Offers & Messaging</Typography>
      <Typography variant="body2" color="text.secondary">
        Tell us about your current offers so we can create compelling campaigns. Select a suggested offer or type your own.
      </Typography>
      <Alert severity="warning" variant="outlined">
        <strong>An evergreen offer is required{verticals.length > 1 ? ' per vertical' : ''}.</strong> This stays consistent across all campaigns. Changing offers frequently causes ad resets that hurt performance. The best-performing offers are typically large percentage-based discounts such as <strong>50% off installation</strong> or <strong>50% off materials</strong>.
      </Alert>

      {verticals.length > 1 ? (
        verticals.map((v) => (
          <Card key={v.id} variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>{v.name}</Typography>
              <Stack spacing={2.5}>
                {renderVerticalOffers(v.id)}
              </Stack>
            </CardContent>
          </Card>
        ))
      ) : verticals.length === 1 ? (
        <Stack spacing={2.5}>
          {renderVerticalOffers(verticals[0].id)}
        </Stack>
      ) : (
        <>
          <TextField
            label="Evergreen Offer *"
            placeholder="e.g., 50% off installation, 50% off materials, Free estimate..."
            value={form.evergreenOffer}
            onChange={(e) => setForm((prev) => ({ ...prev, evergreenOffer: e.target.value }))}
            fullWidth multiline minRows={2}
          />
          <TextField
            label="Financing Offer"
            placeholder="e.g., 0% for 24 months, $99/mo..."
            value={form.financingOffer}
            onChange={(e) => setForm((prev) => ({ ...prev, financingOffer: e.target.value }))}
            fullWidth multiline minRows={2}
          />
        </>
      )}

      {hasBath && (
        <TextField
          select
          label="Bath Manufacturer(s)"
          value={form.bathManufacturers || []}
          onChange={(e) => setForm((prev) => ({ ...prev, bathManufacturers: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value }))}
          fullWidth
          SelectProps={{ multiple: true }}
          helperText="Select the bath manufacturer(s) you work with"
        >
          {BATH_MANUFACTURERS.map((m) => (
            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
          ))}
        </TextField>
      )}
      <SalesScheduleGrid
        value={form.salesSchedule}
        onChange={(val) => setForm((prev) => ({ ...prev, salesSchedule: val }))}
      />
    </Stack>
  );
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIME_SLOTS = [
  '6:00 AM', '6:30 AM', '7:00 AM', '7:30 AM', '8:00 AM', '8:30 AM',
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
  '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM',
  '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM', '9:00 PM',
];

function parseSchedule(str) {
  const schedule = {};
  DAYS.forEach((d) => { schedule[d] = { enabled: false, slots: [] }; });
  if (!str) return schedule;
  // Parse "Monday: 10:00 AM, 2:00 PM, 6:00 PM; Tuesday: 9:00 AM, 1:00 PM" format
  str.split(';').forEach((part) => {
    const trimmed = part.trim();
    const colonIdx = trimmed.indexOf(':');
    if (colonIdx === -1) return;
    const dayStr = trimmed.slice(0, colonIdx).trim();
    const slotsStr = trimmed.slice(colonIdx + 1).trim();
    const day = DAYS.find((d) => d.toLowerCase().startsWith(dayStr.toLowerCase()));
    if (day && slotsStr) {
      const slots = slotsStr.split(',').map((s) => s.trim()).filter(Boolean);
      schedule[day] = { enabled: true, slots };
    }
  });
  return schedule;
}

function serializeSchedule(schedule) {
  return DAYS
    .filter((d) => schedule[d]?.enabled && schedule[d].slots.length > 0)
    .map((d) => `${d}: ${schedule[d].slots.join(', ')}`)
    .join('; ');
}

function SalesScheduleGrid({ value, onChange }) {
  const [schedule, setSchedule] = useState(() => parseSchedule(value));

  const update = (newSchedule) => {
    setSchedule(newSchedule);
    onChange(serializeSchedule(newSchedule));
  };

  const handleToggle = (day) => {
    update({ ...schedule, [day]: { ...schedule[day], enabled: !schedule[day].enabled, slots: schedule[day].slots || [] } });
  };

  const handleAddSlot = (day, time) => {
    if (!time || schedule[day].slots.includes(time)) return;
    const newSlots = [...schedule[day].slots, time].sort((a, b) => {
      return TIME_SLOTS.indexOf(a) - TIME_SLOTS.indexOf(b);
    });
    update({ ...schedule, [day]: { ...schedule[day], slots: newSlots } });
  };

  const handleRemoveSlot = (day, time) => {
    update({ ...schedule, [day]: { ...schedule[day], slots: schedule[day].slots.filter((s) => s !== time) } });
  };

  const applyToAll = (day) => {
    const source = schedule[day].slots;
    const newSchedule = { ...schedule };
    DAYS.forEach((d) => {
      if (newSchedule[d].enabled) {
        newSchedule[d] = { ...newSchedule[d], slots: [...source] };
      }
    });
    update(newSchedule);
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">Sales Schedule / Availability</Typography>
      <Typography variant="caption" color="text.secondary">
        Select the days your team is available and add the time slots when reps can take leads.
      </Typography>
      {DAYS.map((day) => (
        <Box key={day} sx={{ py: 1, px: 1.5, borderRadius: 1, bgcolor: schedule[day].enabled ? 'grey.50' : 'transparent' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FormControlLabel
              control={<Checkbox size="small" checked={schedule[day].enabled} onChange={() => handleToggle(day)} />}
              label={<Typography variant="body2" sx={{ width: 70, fontWeight: schedule[day].enabled ? 600 : 400 }}>{day.slice(0, 3)}</Typography>}
              sx={{ m: 0 }}
            />
            {schedule[day].enabled ? (
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                {schedule[day].slots.map((slot) => (
                  <Chip key={slot} label={slot} size="small" onDelete={() => handleRemoveSlot(day, slot)}
                    sx={{ fontSize: 12 }} />
                ))}
                <TextField
                  select size="small" value="" displayEmpty
                  onChange={(e) => handleAddSlot(day, e.target.value)}
                  sx={{ minWidth: 110 }}
                  SelectProps={{ native: true }}
                >
                  <option value="">+ Add slot</option>
                  {TIME_SLOTS.filter((t) => !schedule[day].slots.includes(t)).map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </TextField>
                {schedule[day].slots.length > 0 && (
                  <Button size="small" onClick={() => applyToAll(day)}
                    sx={{ fontSize: 11, textTransform: 'none', whiteSpace: 'nowrap', ml: 0.5 }}>
                    Apply to all
                  </Button>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.disabled">Closed</Typography>
            )}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

function StepCrm({ form, updateCrm, setForm, clientSlug, clientId }) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);

  const handleInvite = async () => {
    if (!inviteEmail) { toast.error('Email is required'); return; }
    setInviting(true);
    try {
      const [firstName, ...rest] = (inviteName || '').split(' ');
      await defaultClient.request({
        url: 'accounts/auth/client-memberships/invite/',
        method: 'POST',
        payload: {
          email: inviteEmail,
          first_name: firstName || '',
          last_name: rest.join(' ') || '',
          role: 'editor',
          client_id: clientId,
        },
      });
      navigator.clipboard.writeText(`${window.location.origin}/clients/${clientSlug}/webhook-setup`);
      toast.success('Invite sent and webhook link copied!');
      setInviteOpen(false);
      setInviteEmail('');
      setInviteName('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h6">Lead Routing & CRM</Typography>
      <Typography variant="body2" color="text.secondary">
        How should we deliver leads to your team?
      </Typography>
      <TextField
        select
        label="CRM System"
        value={form.crm.choice}
        onChange={updateCrm('choice')}
        fullWidth
      >
        <MenuItem value="">Select your CRM...</MenuItem>
        {CRM_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
        ))}
      </TextField>
      {form.crm.choice === 'other' && (
        <TextField label="CRM Name" value={form.crm.other} onChange={updateCrm('other')} fullWidth />
      )}
      <FormControlLabel
        control={<Checkbox checked={form.crm.emailNotifications} onChange={(e) => setForm((prev) => ({ ...prev, crm: { ...prev.crm, emailNotifications: e.target.checked } }))} />}
        label="Send lead notifications via email"
      />
      {form.crm.emailNotifications && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Add email addresses that should receive lead notifications. For advanced routing (different people per territory/vertical), configure in Lead Delivery.
          </Typography>
          <Stack spacing={1}>
            {(form.crm.notificationEmailList || ['']).map((email, idx) => (
              <Stack key={idx} direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  type="email"
                  placeholder="email@company.com"
                  value={email}
                  onChange={(e) => {
                    const updated = [...(form.crm.notificationEmailList || [''])];
                    updated[idx] = e.target.value;
                    setForm((prev) => ({ ...prev, crm: { ...prev.crm, notificationEmailList: updated, notificationEmails: updated.filter(Boolean).join(', ') } }));
                  }}
                  fullWidth
                  error={!!email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)}
                />
                {(form.crm.notificationEmailList || ['']).length > 1 && (
                  <IconButton size="small" onClick={() => {
                    const updated = (form.crm.notificationEmailList || ['']).filter((_, i) => i !== idx);
                    setForm((prev) => ({ ...prev, crm: { ...prev.crm, notificationEmailList: updated, notificationEmails: updated.filter(Boolean).join(', ') } }));
                  }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            ))}
            <Button
              size="small" startIcon={<AddIcon />}
              onClick={() => {
                const updated = [...(form.crm.notificationEmailList || ['']), ''];
                setForm((prev) => ({ ...prev, crm: { ...prev.crm, notificationEmailList: updated } }));
              }}
              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
            >
              Add another email
            </Button>
          </Stack>
        </Box>
      )}

      <Divider />

      {/* Lead Delivery Setup */}
      <Alert severity="warning" variant="outlined">
        <strong>Lead delivery integration is required.</strong> You or your IT team must configure how leads are delivered to your CRM. Without this, we cannot send you leads.
      </Alert>

      {clientSlug && (
        <Card variant="outlined" sx={{ bgcolor: '#fff8f0', border: '2px solid #ff680d' }}>
          <CardContent sx={{ py: 2.5 }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>Configure Lead Delivery</Typography>
              <Typography variant="body2" color="text.secondary">
                Set up your webhook URL, map your CRM fields, and send a test lead to verify everything works. You'll need your CRM's webhook URL and any API credentials.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This can be done now or anytime from your portal under <strong>Lead Delivery</strong> in the sidebar. If your IT team handles integrations, share this link with them.
              </Typography>
              <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                <Button
                  variant="contained"
                  href={`/clients/${clientSlug}/webhook-setup`}
                  target="_blank"
                  sx={{ bgcolor: '#ff680d', '&:hover': { bgcolor: '#ea580c' } }}
                >
                  Configure Now
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setInviteOpen(true)}
                >
                  Invite IT Team Member
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Invite IT team modal */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Invite IT Team Member</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Enter your IT person's email to invite them to the portal. They'll receive an email to set their password, then can configure lead delivery at the webhook setup page.
            </Typography>
            <TextField
              label="Their Name"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              fullWidth
              size="small"
              placeholder="John Smith"
            />
            <TextField
              label="Their Email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              fullWidth
              size="small"
              required
              placeholder="it@yourcompany.com"
            />
            <Alert severity="info" variant="outlined" sx={{ fontSize: 13 }}>
              They will receive an email to create their password. Once logged in, they can find <strong>Lead Delivery</strong> in the sidebar to configure your webhook integration.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
          <Button
            onClick={handleInvite}
            variant="contained"
            disabled={inviting || !inviteEmail}
          >
            {inviting ? 'Sending...' : 'Send Invite & Copy Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function StepLaunch({ form, setForm }) {
  return (
    <Stack spacing={3}>
      <Typography variant="h6">Target Launch Date</Typography>
      <Typography variant="body2" color="text.secondary">
        When would you like your campaigns to go live? We typically need 7-10 business days after all onboarding is complete.
      </Typography>
      <DatePicker
        label="Desired Launch Date"
        value={form.launchDate}
        onChange={(val) => setForm((prev) => ({ ...prev, launchDate: val }))}
        slotProps={{ textField: { fullWidth: true } }}
        minDate={dayjs().add(7, 'day')}
      />
    </Stack>
  );
}

function LandingPageCard({ lp, submitting, onAction }) {
  const [commentText, setCommentText] = useState('');
  const comments = lp.comments || [];
  const isPending = lp.status === 'pending';
  const isChangesRequested = lp.status === 'changes_requested';

  const statusLabel = lp.approved ? 'Approved' : isChangesRequested ? 'Changes Requested' : 'Pending Review';
  const statusColor = lp.approved ? 'success' : isChangesRequested ? 'warning' : 'default';

  return (
    <Box sx={{
      p: 2, borderRadius: 1.5,
      border: '1px solid',
      borderColor: lp.approved ? 'success.main' : isChangesRequested ? '#f59e0b' : '#e0e0e0',
      bgcolor: lp.approved ? '#f0fdf4' : isChangesRequested ? '#fffbeb' : '#fff',
    }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={lp.vertical || 'General'} size="small"
              sx={{ bgcolor: '#111827', color: '#fff', fontWeight: 700, fontSize: 11 }} />
            <Typography variant="body2" fontWeight={600}>
              {lp.name || 'Landing Page'}
            </Typography>
            <Chip label={statusLabel} size="small" color={statusColor}
              sx={{ fontSize: 11, height: 20 }} />
          </Stack>
          <Typography
            variant="caption" color="primary" component="a"
            href={lp.url} target="_blank" rel="noopener noreferrer"
            sx={{ display: 'block', mt: 0.5, wordBreak: 'break-all', textDecoration: 'underline', cursor: 'pointer' }}
          >
            {lp.url}
          </Typography>
          {lp.approved && lp.approved_by_name && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Approved by {lp.approved_by_name} on {new Date(lp.approved_at).toLocaleDateString()}
            </Typography>
          )}
        </Box>
        {isPending && (
          <Button
            size="small" variant="contained" disabled={submitting}
            sx={{ ml: 2, flexShrink: 0, bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}
            onClick={() => onAction('approve')}
          >
            Approve
          </Button>
        )}
      </Stack>

      {/* Comment thread */}
      {comments.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1.5, maxHeight: 200, overflowY: 'auto' }}>
          {comments.map((c, i) => (
            <Box key={i} sx={{
              p: 1.5, borderRadius: 1.5,
              bgcolor: c.role === 'guru' ? '#f0f7ff' : '#f5f5f5',
              borderLeft: c.role === 'guru' ? '3px solid #2563eb' : '3px solid #ff680d',
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" fontWeight={600}>
                  {c.author} {c.role === 'guru' ? '(Lead Gurus)' : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {c.at ? new Date(c.at).toLocaleDateString() : ''}
                </Typography>
              </Stack>
              {c.text && <Typography variant="body2" sx={{ mt: 0.5 }}>{c.text}</Typography>}
            </Box>
          ))}
        </Stack>
      )}

      {/* Action / reply input */}
      {!lp.approved && (
        <Box sx={{ mt: 1.5 }}>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              placeholder={isPending ? 'Request changes or leave a comment...' : 'Add a reply...'}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              fullWidth
              onKeyDown={(e) => {
                if (e.key === 'Enter' && commentText.trim()) {
                  onAction(isPending ? 'request_changes' : 'comment', commentText);
                  setCommentText('');
                }
              }}
            />
            <Button
              size="small"
              variant="outlined"
              color={isPending ? 'warning' : 'primary'}
              disabled={!commentText.trim() || submitting}
              onClick={() => {
                onAction(isPending ? 'request_changes' : 'comment', commentText);
                setCommentText('');
              }}
            >
              {isPending ? 'Send' : 'Reply'}
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}

export function CreativeCarousel({ assets, clientSlug, onRefresh }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  if (!assets.length) return null;
  const active = assets[activeIdx] || assets[0];
  const canAct = active.asset_status === 'pending';
  const comments = active.comments || [];

  const handleAction = async (action, text) => {
    setSubmitting(true);
    try {
      await defaultClient.request({
        url: `app1/creative-review/${clientSlug}/asset/${active.id}/action/`,
        method: 'POST',
        payload: { action, text: text || '' },
      });
      toast.success(action === 'approve' ? 'Approved!' : action === 'request_changes' ? 'Feedback sent!' : 'Comment added!');
      setCommentText('');
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      {/* Main display */}
      <Box sx={{ position: 'relative' }}>
        <Box sx={{
          border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden',
          bgcolor: '#fafafa', height: 360,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {active.asset_type === 'video' ? (
            <video key={active.id} src={active.file_url} controls
              style={{ maxWidth: '100%', maxHeight: '100%', display: 'block' }} />
          ) : (
            <img key={active.id} src={active.file_url} alt={active.label}
              style={{ maxWidth: '100%', maxHeight: '100%', display: 'block', objectFit: 'contain' }} />
          )}
        </Box>

        {/* Vertical badge overlay */}
        {active.batch?.vertical_name && (
          <Chip
            label={active.batch.vertical_name}
            size="small"
            sx={{
              position: 'absolute', top: 12, left: 12,
              bgcolor: '#111827', color: '#fff', fontWeight: 700, fontSize: 12,
              letterSpacing: 0.3, height: 28,
            }}
          />
        )}

        {assets.length > 1 && (
          <>
            <Box onClick={() => { setActiveIdx((prev) => (prev - 1 + assets.length) % assets.length); setCommentText(''); }}
              sx={{
                position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.5)', borderRadius: '50%', width: 40, height: 40,
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 20, lineHeight: 1 }}>&#8249;</Typography>
            </Box>
            <Box onClick={() => { setActiveIdx((prev) => (prev + 1) % assets.length); setCommentText(''); }}
              sx={{
                position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.5)', borderRadius: '50%', width: 40, height: 40,
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 20, lineHeight: 1 }}>&#8250;</Typography>
            </Box>
          </>
        )}
      </Box>

      {/* Ad copy preview — labeled like creative performance */}
      {(active.headline || active.primary_text) && (
        <Box sx={{ mt: 1.5, p: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fff' }}>
          {active.headline && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Headline</Typography>
              <Typography variant="subtitle1" fontWeight={700}>{active.headline}</Typography>
            </Box>
          )}
          {active.primary_text && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Primary Text</Typography>
              <Typography variant="body2">{active.primary_text}</Typography>
            </Box>
          )}
          {active.description && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Description</Typography>
              <Typography variant="body2" color="text.secondary">{active.description}</Typography>
            </Box>
          )}
          {(active.display_url || active.cta_type) && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              {active.display_url && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Destination</Typography>
                  <Typography variant="body2" color="primary" sx={{ wordBreak: 'break-all' }}>{active.display_url}</Typography>
                </Box>
              )}
              {active.cta_type && (
                <Chip label={active.cta_type.replace(/_/g, ' ')} size="small" color="primary" variant="outlined" sx={{ fontSize: 11 }} />
              )}
            </Stack>
          )}
        </Box>
      )}

      {/* Label + status + dots */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight={500}>
            {active.label || `Creative ${activeIdx + 1}`}
          </Typography>
          <Chip
            label={active.asset_status === 'approved' ? 'Approved' : active.asset_status === 'changes_requested' ? 'Changes Requested' : 'Pending'}
            size="small"
            color={active.asset_status === 'approved' ? 'success' : active.asset_status === 'changes_requested' ? 'warning' : 'default'}
          />
        </Stack>
        {assets.length > 1 && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {assets.map((a, idx) => (
              <Box
                key={idx}
                onClick={() => { setActiveIdx(idx); setCommentText(''); }}
                sx={{
                  width: idx === activeIdx ? 16 : 8, height: 8, borderRadius: 4,
                  bgcolor: idx === activeIdx ? '#ff680d' : a.asset_status === 'approved' ? '#16a34a' : a.asset_status === 'changes_requested' ? '#f59e0b' : '#ddd',
                  cursor: 'pointer', transition: 'all 0.2s',
                }}
              />
            ))}
          </Stack>
        )}
      </Stack>

      {/* Comments thread */}
      {comments.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1.5, maxHeight: 200, overflowY: 'auto' }}>
          {comments.map((c, i) => (
            <Box key={i} sx={{
              p: 1.5, borderRadius: 1.5,
              bgcolor: c.role === 'guru' ? '#f0f7ff' : '#f5f5f5',
              borderLeft: c.role === 'guru' ? '3px solid #2563eb' : '3px solid #ff680d',
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" fontWeight={600}>
                  {c.author} {c.role === 'guru' ? '(Lead Gurus)' : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {c.at ? new Date(c.at).toLocaleDateString() : ''}
                </Typography>
              </Stack>
              {c.text && <Typography variant="body2" sx={{ mt: 0.5 }}>{c.text}</Typography>}
            </Box>
          ))}
        </Stack>
      )}

      {/* Action buttons + comment input */}
      {canAct && (
        <Box sx={{ mt: 1.5 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Button
              variant="contained" size="small" disabled={submitting}
              sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}
              onClick={() => handleAction('approve')}
            >
              Approve
            </Button>
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small" placeholder="Request changes or leave a comment..."
              value={commentText} onChange={(e) => setCommentText(e.target.value)}
              fullWidth
              onKeyDown={(e) => { if (e.key === 'Enter' && commentText.trim()) handleAction('request_changes', commentText); }}
            />
            <Button
              size="small" variant="outlined" color="warning" disabled={!commentText.trim() || submitting}
              onClick={() => handleAction('request_changes', commentText)}
            >
              Send
            </Button>
          </Stack>
        </Box>
      )}

      {active.asset_status === 'changes_requested' && (
        <Box sx={{ mt: 1.5 }}>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small" placeholder="Add a reply..."
              value={commentText} onChange={(e) => setCommentText(e.target.value)}
              fullWidth
              onKeyDown={(e) => { if (e.key === 'Enter' && commentText.trim()) handleAction('comment', commentText); }}
            />
            <Button
              size="small" variant="outlined" disabled={!commentText.trim() || submitting}
              onClick={() => handleAction('comment', commentText)}
            >
              Reply
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}

const STEP_KEY_TO_INDEX = { basics: 0, billing: 1, contract: 1, assets: 2, offers: 3, crm: 4, launch: 5, approvals: 6 };

function StepReview({ completionSteps, clientName, progress, onGoToStep }) {
  const allDone = completionSteps.every((s) => s.completed);
  return (
    <Stack spacing={3}>
      <Typography variant="h6">Review</Typography>
      {allDone ? (
        <Alert severity="success">
          All sections are complete for <strong>{clientName}</strong>. Our team will begin building your campaigns.
          You'll be notified as we make progress.
        </Alert>
      ) : (
        <Alert severity="warning">
          Some sections are still incomplete. Click any section below to jump to it.
        </Alert>
      )}
      <Stack spacing={1.5}>
        {completionSteps.map((step) => (
          <Stack key={step.key} direction="row" spacing={1.5} alignItems="center"
            onClick={() => {
              const idx = STEP_KEY_TO_INDEX[step.key];
              if (idx != null && onGoToStep) onGoToStep(idx);
            }}
            sx={{
              p: 1.5, borderRadius: 1, cursor: 'pointer',
              bgcolor: step.completed ? 'success.50' : 'grey.50',
              '&:hover': { bgcolor: step.completed ? 'success.100' : 'grey.100' },
            }}>
            {step.completed ? (
              <CheckCircleIcon sx={{ color: 'success.main' }} />
            ) : (
              <RadioButtonUncheckedIcon sx={{ color: 'warning.main' }} />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>{step.label}</Typography>
              <Typography variant="caption" color="text.secondary">{step.description}</Typography>
            </Box>
            <Chip label={step.completed ? 'Complete' : 'Incomplete'} size="small"
              color={step.completed ? 'success' : 'warning'} />
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}
